import { Component, OnInit, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { ApModuleControlService } from "@tylertech/accounts-payable-module";
import { ICardAction } from "@tylertech/tyler-api-types";

import { PacketService } from "../../services/packet.service";

export interface PacketDetailDialogData
{
    readonly aggregateId: string;
    readonly alternateType: string;
    readonly cardActions: ICardAction[];
    readonly packetId: string;
    readonly packetNumber: string;
    readonly packetType: string;
}

@Component({
    selector: "packet-detail-dialog-component",
    templateUrl: "./packetDetailDialog.tmpl.html",
    styleUrls: [
        "./packetDetailDialog.component.scss"
    ],
    standalone: false
})
export class PacketDetailDialogComponent implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<PacketDetailDialogComponent, ICardAction | undefined>>(MatDialogRef);
    private readonly data = inject<PacketDetailDialogData>(MAT_DIALOG_DATA);
    private readonly $packetService = inject(PacketService);
    private readonly $apModuleControlService = inject(ApModuleControlService);

    detail: unknown;

    apModuleControl: Record<string, unknown>;

    isLoaded: boolean;

    ngOnInit(): void
    {
        this.dialogRef.updateSize("80vw");

        Promise.all(
            [

                this.$packetService.GetPacketDetails(
                    {
                        PacketId: this.packetId,
                        AggregateProviderId: this.aggregateId,
                        PacketProcessType: this.packetType,
                        AlternateType: this.alternateType,
                    })
                    .then((result) =>
                    {
                        this.detail = result;
                    }),

                this.$apModuleControlService.GetSettings()
                    .then((result: Record<string, unknown>) =>
                    {
                        this.apModuleControl = result;
                    })
            ])
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private get packetId(): string { return this.data.packetId; }
    private get aggregateId(): string { return this.data.aggregateId; }
    private get packetType(): string { return this.data.packetType; }
    private get packetNumber(): string { return this.data.packetNumber; }
    get cardActions(): ICardAction[] { return this.data.cardActions; }
    private get alternateType(): string { return this.data.alternateType; }

    get isAPPayable(): boolean
    {
        return this.packetType === "APPayable"
            || (this.packetType === "APDirect"
                && this.alternateType === "APDirectPayable");
    }

    get isAPPayment(): boolean
    {
        return this.packetType === "APPayment"
            || (this.packetType === "APDirect"
                && this.alternateType === "APDirectPayment");
    }

    get isPOReceipt(): boolean
    {
        return this.packetType === "POReceipt";
    }

    get isGLJournalEntry(): boolean
    {
        return this.packetType === "GLJournal";
    }

    get title(): string
    {
        switch (this.packetType)
        {
            case "APPayable":
                return "AP Payable Packet " + this.packetNumber;
            case "APPayment":
                if (this.apModuleControl && !this.apModuleControl.APPaymentDetailMessage)
                {
                    return "AP Payment Packet " + this.packetNumber;
                }
                else
                {
                    return "AP Voucher " + this.packetNumber;
                }
            case "APDirect":
                return "AP Direct Packet " + this.packetNumber;
            case "POReceipt":
                return "PO Receipt Packet " + this.packetNumber;
            case "GLJournal":
                return "JE Approval Packet " + this.packetNumber;
        }
    }

    getCloseActionAriaLabel(): string
    {
        return `Submission-Close-${this.title}`;
    }

    getActionAriaLabel(action: ICardAction): string
    {
        return `Edit-${action.label}-${this.title}`;
    }

    getActionStyle(action: ICardAction): Record<string, string>
    {
        return { 'fill': action.color };
    }
}
